import React, { useEffect, useMemo, useReducer, useState } from 'react';

import { SelectChangeEvent } from '@mui/material';

import i18n from 'i18next';

import { useGetLanguages } from '../../../../redux/api/translationsAPI';
import { Language } from '../../../../shared/constants';
import {
  LocalStorageKey,
  LocalStorageService,
} from '../../../../shared/storage/local-storage';
import LanguageSelectorShell from './LanguageSelectorShell';

interface IAuthLanguageSelectorProps {
  className?: string;
}

const AuthLanguageSelector = ({ className }: IAuthLanguageSelectorProps) => {
  const [i18nTick, rerender] = useReducer((n: number) => n + 1, 0);
  const [selectedKey, setSelectedKey] = useState('');
  const { data: languages, isLoading: isLanguagesLoading } = useGetLanguages();

  useEffect(() => {
    const onLanguageChanged = () => {
      rerender();
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  const i18nSelectedKey = useMemo(() => {
    if (!languages?.length) return '';
    const code = i18n.language?.split('-')[0] ?? '';
    return languages.find((l) => l.key === code)?.key ?? languages[0].key;
  }, [languages, i18nTick]);

  useEffect(() => {
    setSelectedKey(i18nSelectedKey);
  }, [i18nSelectedKey]);

  const onChange = async (event: SelectChangeEvent<string>) => {
    const nextKey = event.target.value;
    if (!languages?.some((l) => l.key === nextKey)) return;

    setSelectedKey(nextKey);
    LocalStorageService.setValue(LocalStorageKey.language, nextKey as Language);
    await i18n.changeLanguage(nextKey);
  };

  if (isLanguagesLoading || !languages?.length) {
    return null;
  }

  return (
    <LanguageSelectorShell
      languages={languages}
      selectedKey={selectedKey}
      onChange={onChange}
      className={className}
    />
  );
};

export default AuthLanguageSelector;
